import { createChart } from 'lightweight-charts'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { useTheme } from 'styled-components'

import { RoundedCandleSeries } from '@root/shared/lib/lightweight-charts'

import type { ClassNameProps } from '@root/shared/react'
import type BigNumber from 'bignumber.js'
import type { CandlestickData, IChartApi, ISeriesApi } from 'lightweight-charts'

export type DataEntry = [Date, BigNumber]

type Props = ClassNameProps & {
  candles: Array<CandlestickData>
  radius?: (barSpacing: number) => number
}
export const PriceChartThumbnail: React.FC<Props> = ({
  className,
  candles,
  radius = undefined,
}) => {
  const { t } = useTranslation()
  t!
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | undefined>(undefined)
  const seriesRef = useRef<ISeriesApi<'Custom'> | undefined>(undefined)
  const [initError, setInitError] = useState<Error | undefined>(undefined)

  const theme = useTheme()

  useEffect(() => {
    const element = containerRef.current
    if (!element) {
      return
    }

    try {
      // Pass explicit dimensions and disable autoSize so the chart starts at
      // the correct size synchronously, instead of flashing at 0px while we
      // wait for autoSize's ResizeObserver to fire (visible in popovers and
      // tooltips that mount lazily). Our chart containers are fixed-size, so
      // we don't need autoSize to track future container resizes.
      const chart = createChart(element, {
        ...RoundedCandleSeries.chartOptions(),
        autoSize: false,
        width: element.clientWidth,
        height: element.clientHeight,
      })

      chartRef.current = chart

      const series = chart.addCustomSeries(new RoundedCandleSeries(), {
        upColor: theme.palette.candleGreen[9],
        downColor: theme.palette.candleRed[9],
        radius: radius || (() => 3),
      })

      seriesRef.current = series
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('PriceChartThumbnail')
      // eslint-disable-next-line no-console
      console.dir(error)
      setInitError(error instanceof Error ? error : new Error(String(error)))
    }

    return () => {
      chartRef.current?.remove()
      chartRef.current = undefined
      seriesRef.current = undefined

      setInitError(undefined)
    }
  }, [theme, radius])

  useEffect(() => {
    const series = seriesRef.current
    const chart = chartRef.current
    if (!series || !chart) {
      return
    }
    series.setData(candles)
    chart.timeScale().fitContent()
  }, [candles])

  return initError ? (
    <ErrorRoot className={className}>
      {/* <ErrorMessage>{t('common.something-went-wrong')}</ErrorMessage> */}
    </ErrorRoot>
  ) : (
    <Root className={className} ref={containerRef} />
  )
}

const Root = styled.div`
  position: relative;
  will-change: filter;
  contain: layout;
  transform: translateZ(0);
  perspective: 1000;
  backface-visibility: hidden;
  will-change: filter, transform;
  width: 100%;
  height: 100%;
`
const ErrorRoot = styled(Root)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
`
// const ErrorMessage = styled.div`
//   position: absolute;
//   color: var(--error-9);
//   top: 50%;
// `
